import { z } from "zod";
import { base64ToBytes, bytesToBase64 } from "./base64.js";
import { CodecError } from "./codec.js";

/**
 * The key a space is encrypted with, and how it reaches the second device
 * (SPEC §3, decision 2 — the extension from `none` to `e2ee`).
 *
 * ## The shape of the problem
 *
 * Two people, two phones, one shared space, and a server that must never be
 * able to read any of it — including when the person who set it up is asleep
 * and the other one is signing in for the first time on a new phone.
 *
 * That rules out the obvious answers. Sending the key through the server
 * defeats the point. Generating it from the account password would hand it to
 * whoever runs the login endpoint. Typing a 256-bit key across by hand is not
 * something people do twice.
 *
 * ## What this does instead
 *
 * The space has a random key, which is what actually encrypts content. That key
 * is then **wrapped** with a second key derived from a passphrase the two
 * people agree between themselves — out loud, once — and the wrapped blob is
 * stored on the server. The server holds something it cannot open. A new device
 * fetches the blob and needs only the passphrase to unwrap it.
 *
 * The passphrase is never sent anywhere, and neither is the space key.
 *
 * ## Why the app generates that phrase rather than asking for one
 *
 * A phrase two people agree on is the one real attack: whoever takes the
 * database can grind at the wrapped blob offline for as long as they like, and
 * a phrase people can hold in their heads does not survive that. So the app
 * mints a **recovery code** instead — 138 bits out of the system generator,
 * which there is nothing to grind at. It is shown once and belongs in a
 * password manager.
 *
 * Day to day nobody types it. The other person's device is handed the key
 * directly, by a QR code the first device draws (`exportSpaceKey` below): the
 * key rides in the URL **fragment**, which browsers never put on the wire, so
 * the handover happens between two phones in one room and the server sees an
 * ordinary invite. The recovery code is the way back when there is no phone
 * left to scan.
 *
 * ## What this costs, said plainly
 *
 * **If the code is lost and no device still holds the key, the content is
 * gone.** Not recoverable by us, not recoverable by the person with root on the
 * server — that is the guarantee, stated from the other side. Any screen that
 * turns this on has to say so in those words, and it is why turning it on is a
 * deliberate act rather than a default.
 */

/**
 * PBKDF2, because it is what WebCrypto has.
 *
 * Argon2id would be the better answer and is not available without shipping
 * WASM to every device. Given PBKDF2-SHA256, the defence is iterations: this is
 * the OWASP figure, and it costs roughly a third of a second on a phone — paid
 * once when a device is set up, never during use.
 */
export const KDF_ITERATIONS = 600_000;

const SALT_BYTES = 16;
const IV_BYTES = 12;
const KEY_BITS = 256;

export const WRAPPED_KEY_VERSION = 1;

/**
 * The wrapped key as it is stored and moved around.
 *
 * Carries its own salt and iteration count rather than assuming today's
 * constants: a space wrapped years ago has to keep opening after the numbers
 * here are raised.
 */
export const wrappedKeySchema = z.object({
  v: z.literal(WRAPPED_KEY_VERSION),
  salt: z.string().min(1),
  iv: z.string().min(1),
  ct: z.string().min(1),
  iterations: z.number().int().positive(),
});

export type WrappedKey = z.infer<typeof wrappedKeySchema>;

const subtle = (): SubtleCrypto => {
  if (typeof crypto === "undefined" || !crypto.subtle) {
    throw new CodecError("WebCrypto is unavailable — a secure context is required");
  }
  return crypto.subtle;
};

/** A fresh key for a space. Extractable, because it has to be wrapped. */
export async function generateSpaceKey(): Promise<CryptoKey> {
  return subtle().generateKey({ name: "AES-GCM", length: KEY_BITS }, true, [
    "encrypt",
    "decrypt",
  ]);
}

/**
 * Turn a passphrase into the key that wraps the space key.
 *
 * Separate from the space key on purpose. Encrypting content with a
 * passphrase-derived key directly would mean that changing the passphrase
 * re-encrypts every row in the space; wrapping means it rewrites one blob.
 */
async function deriveWrappingKey(
  passphrase: string,
  salt: Uint8Array,
  iterations: number,
): Promise<CryptoKey> {
  const material = await subtle().importKey(
    "raw",
    new TextEncoder().encode(passphrase),
    "PBKDF2",
    false,
    ["deriveKey"],
  );

  return subtle().deriveKey(
    { name: "PBKDF2", salt: salt as BufferSource, iterations, hash: "SHA-256" },
    material,
    { name: "AES-GCM", length: KEY_BITS },
    false,
    ["encrypt", "decrypt"],
  );
}

/**
 * Wrap a space key so the server can hold it without being able to use it.
 *
 * `iterations` is a parameter only so tests can run in finite time. Production
 * callers pass nothing.
 */
export async function wrapSpaceKey(
  key: CryptoKey,
  passphrase: string,
  iterations: number = KDF_ITERATIONS,
): Promise<WrappedKey> {
  if (passphrase.length === 0) {
    throw new CodecError("refusing to wrap a space key with an empty passphrase");
  }

  const salt = crypto.getRandomValues(new Uint8Array(SALT_BYTES));
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const wrapping = await deriveWrappingKey(passphrase, salt, iterations);

  const raw = await subtle().exportKey("raw", key);
  const ciphertext = await subtle().encrypt(
    { name: "AES-GCM", iv: iv as BufferSource },
    wrapping,
    raw,
  );

  return {
    v: WRAPPED_KEY_VERSION,
    salt: bytesToBase64(salt),
    iv: bytesToBase64(iv),
    ct: bytesToBase64(new Uint8Array(ciphertext)),
    iterations,
  };
}

/**
 * Open a wrapped key with the passphrase.
 *
 * The result is **extractable**, and that is a deliberate reversal of what this
 * function used to do. A non-extractable key cannot be handed to the other
 * person's phone, which means every new device would have to go through the
 * recovery code — the thing that is supposed to be filed away and never typed.
 *
 * What non-extractability actually buys is narrower than it sounds: script
 * running in this origin can already read every list, message and wish through
 * the key, extractable or not. It stops the raw bytes leaving, not the content.
 * Trading that for a handover people will really use is the better bargain, and
 * it is why the key never leaves IndexedDB except into a QR code somebody is
 * looking at.
 */
export async function unwrapSpaceKey(
  wrapped: WrappedKey,
  passphrase: string,
): Promise<CryptoKey> {
  const wrapping = await deriveWrappingKey(
    passphrase,
    base64ToBytes(wrapped.salt),
    wrapped.iterations,
  );

  let raw: ArrayBuffer;
  try {
    raw = await subtle().decrypt(
      { name: "AES-GCM", iv: base64ToBytes(wrapped.iv) as BufferSource },
      wrapping,
      base64ToBytes(wrapped.ct),
    );
  } catch {
    // GCM authentication failed. The overwhelmingly likely cause is a wrong
    // passphrase, and saying so is more useful than "decryption error" — the
    // ciphertext being tampered with is indistinguishable from here anyway.
    throw new CodecError("wrong passphrase for this space");
  }

  return subtle().importKey("raw", raw, { name: "AES-GCM" }, true, [
    "encrypt",
    "decrypt",
  ]);
}

/* ------------------------------------------------------------ handing over */

/**
 * The key as it travels between two phones in one room.
 *
 * base64url rather than base64: this string's only destination is the fragment
 * of a URL inside a QR code, and `+` and `/` there are asking for trouble from
 * every scanner and share sheet it passes through.
 */
export async function exportSpaceKey(key: CryptoKey): Promise<string> {
  const raw = new Uint8Array(await subtle().exportKey("raw", key));
  return bytesToBase64(raw).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

/** The other end of `exportSpaceKey`. Throws on anything that is not a key. */
export async function importSpaceKey(material: string): Promise<CryptoKey> {
  const padded = material.replaceAll("-", "+").replaceAll("_", "/");
  let raw: Uint8Array;
  try {
    raw = base64ToBytes(padded + "=".repeat((4 - (padded.length % 4)) % 4));
  } catch {
    throw new CodecError("this is not a space key");
  }

  if (raw.length !== KEY_BITS / 8) throw new CodecError("this is not a space key");

  return subtle().importKey("raw", raw as BufferSource, { name: "AES-GCM" }, true, [
    "encrypt",
    "decrypt",
  ]);
}

/* ---------------------------------------------------------- recovery codes */

/**
 * The alphabet a person has to copy off a screen without help.
 *
 * No `I`, `L`, `O`, `0` or `1`: this is read aloud, retyped, and written down
 * on paper, and the pairs that look alike cost more in support than the two
 * bits they add.
 */
const RECOVERY_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";
const RECOVERY_SYMBOLS = 28;
const RECOVERY_GROUP = 4;

/**
 * A code with 138 bits behind it — `31 ** 28` — and nothing for an offline
 * search to bite on.
 *
 * Rejection sampling rather than `% 31`, which would make eight of the
 * thirty-one symbols a shade likelier than the rest. That bias is harmless in
 * an invite code and is not the sort of thing to leave in key material.
 */
export function generateRecoveryCode(): string {
  const limit = 256 - (256 % RECOVERY_ALPHABET.length);
  const symbols: string[] = [];

  while (symbols.length < RECOVERY_SYMBOLS) {
    for (const value of crypto.getRandomValues(new Uint8Array(RECOVERY_SYMBOLS))) {
      if (value >= limit) continue;
      symbols.push(RECOVERY_ALPHABET[value % RECOVERY_ALPHABET.length]!);
      if (symbols.length === RECOVERY_SYMBOLS) break;
    }
  }

  const groups: string[] = [];
  for (let i = 0; i < symbols.length; i += RECOVERY_GROUP) {
    groups.push(symbols.slice(i, i + RECOVERY_GROUP).join(""));
  }

  return groups.join("-");
}

/**
 * What somebody typed, as the code they meant.
 *
 * Spaces, dashes and case are how a person copies eight groups off a screen,
 * not part of the secret — and a code refused because it was pasted with a
 * trailing space is a support call about lost data.
 */
export function normaliseRecoveryCode(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

/**
 * Change the passphrase without touching a single row of content.
 *
 * The whole reason the space key and the passphrase are two different things:
 * this rewrites one blob, where a passphrase-derived content key would mean
 * re-encrypting everything the couple has ever written.
 */
export async function rewrapSpaceKey(
  wrapped: WrappedKey,
  currentPassphrase: string,
  nextPassphrase: string,
  iterations: number = KDF_ITERATIONS,
): Promise<WrappedKey> {
  const key = await unwrapSpaceKey(wrapped, currentPassphrase);

  return wrapSpaceKey(key, nextPassphrase, iterations);
}
